import { useEffect, useState } from "react";

import {
    ALL_ROLES_ELIGIBLE,
    BUSINESS_ROLES_PER_TEAM_TYPE,
    BUSINESS_TEAM_TYPES,
    USER_SEGMENTATION_CLIENT_MAPPING,
    USER_SEGMENTATION_MAPPING_NONE,
    USER_SEGMENTATION_MAPPING_UNKNOWN,
    UserAccountData,
    UserSegmentData,
    UserSegmentationRoleTypes,
    UserSegmentationTeamTypes
} from "../models/user-segmentation";

const USER_SEG_ENDPOINT = "/gateway/api/me";

const getNormalizedEntry = (value: string) =>
    Object.entries(USER_SEGMENTATION_CLIENT_MAPPING).find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ([, translatedValues]: [any, string[]]) =>
            translatedValues.map((value) => value.toLowerCase()).includes(value)
    );

const normalizeUserSegmentationString = (value: string): string => {
    if (value === "") {
        return USER_SEGMENTATION_MAPPING_NONE;
    }
    const normalizedEntry = getNormalizedEntry(value.toLowerCase());
    return normalizedEntry !== undefined
        ? normalizedEntry[0]
        : USER_SEGMENTATION_MAPPING_UNKNOWN;
};

export const isUserInBusinessCategory = (
    userAccountData: UserAccountData | null
): boolean => {
    if (!userAccountData) {
        return false;
    }
    const { teamType = "", role = "" } = userAccountData;

    const normalizedTeamType = normalizeUserSegmentationString(
        teamType
    ) as UserSegmentationTeamTypes;
    const normalizedRole = normalizeUserSegmentationString(
        role
    ) as UserSegmentationRoleTypes;

    const isTeamTypeEligible = BUSINESS_TEAM_TYPES.includes(normalizedTeamType);
    const isRoleEligible = Boolean(
        BUSINESS_ROLES_PER_TEAM_TYPE[normalizedTeamType]?.includes(
            ALL_ROLES_ELIGIBLE
        ) ||
            BUSINESS_ROLES_PER_TEAM_TYPE[normalizedTeamType]?.includes(
                normalizedRole
            )
    );
    return isTeamTypeEligible && isRoleEligible;
};

const getUserAccountData = async (): Promise<UserAccountData | null> => {
    try {
        const {
            extended_profile: { job_title: jobTitle, team_type: teamType }
        } = await fetch(USER_SEG_ENDPOINT).then((res) => res.json());

        return {
            role: jobTitle,
            teamType
        };
    } catch (error) {
        return null;
    }
};

const fetchUserSegmentData = async () => {
    const userAccountData = await getUserAccountData();
    const isBusinessSegmentUser = isUserInBusinessCategory(userAccountData);
    const fetchError = userAccountData === null;
    return {
        userAccountData,
        isBusinessSegmentUser,
        fetchError,
        fetched: true
    };
};

export const useFetchUserSegmentData = (shouldFetch: boolean) => {
    const initialState: UserSegmentData = {
        userAccountData: null,
        isBusinessSegmentUser: false,
        fetchError: false,
        fetched: false
    };
    const [state, setState] = useState(initialState);
    useEffect(() => {
        if (shouldFetch) {
            fetchUserSegmentData()
                .then((fetchedState) => {
                    setState(fetchedState);
                })
                .catch(() => {
                    setState({
                        userAccountData: null,
                        isBusinessSegmentUser: false,
                        fetchError: true,
                        fetched: true
                    });
                });
        } else {
            setState(initialState);
        }
    }, [shouldFetch]);
    return state;
};
